import React, { useState, useEffect, useCallback } from 'react';
import FlowEditor from './FlowEditor';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import AppLoadingState from './common/AppLoadingState';
import { DialogFlowPayloadManager } from '../utils/dialogFlowPayloadManager';

const DialogFlowDetails = ({ 
  dialogFlowId, 
  onClose, 
  edges,
  setEdges,
  onEdgeLabelChange 
}) => {
  const navigate = useNavigate();
  const [dialogFlow, setdialogFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchdialogFlow = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getdialogFlow(dialogFlowId);
      
      const firstNode = response.nodes[0];
      const globalPrompt = firstNode?.data?.globalPrompt || '';
      
      const dialogFlowData = {
        ...response,
        nodes: (response.nodes || []).map((node, index) => ({
          ...node,
          position: node.position || { x: index * 250, y: index * 100 },
        })),
        edges: (response.edges || []).map(edge => ({
          ...edge,
          type: 'custom',
          label: edge.data?.label || edge.label || 'User Responded',
          data: {
            ...edge.data,
            label: edge.data?.label || edge.label || 'User Responded'
          }
        })),
        globalPrompt
      };
      
      setdialogFlow(dialogFlowData);
      setError(null);
    } catch (err) {
      console.error('Error fetching dialogFlow details:', err);
      setError('Failed to load dialogFlow details');
    } finally {
      setLoading(false);
    }
  }, [dialogFlowId]);

  useEffect(() => {
    if (dialogFlowId) {
      fetchdialogFlow();
    }
  }, [dialogFlowId, fetchdialogFlow]);

  const handleSave = async (flowData) => {
    try {
      setIsSaving(true);
      const formattedEdges = flowData.edges.map(edge => ({
        ...edge,
        label: edge.data?.label || edge.label || 'User Responded',
        data: {
          ...edge.data,
          label: edge.data?.label || edge.label || 'User Responded'
        }
      }));

      const updatedNodes = flowData.nodes.map(node => {
        return DialogFlowPayloadManager.createNodePayload({
          ...node,
          data: {
            ...node.data,
            globalPrompt: flowData.globalPrompt
          }
        });
      });

      await toast.promise(
        api.updateDialogFlow(dialogFlowId, {
          ...dialogFlow,
          nodes: updatedNodes,
          edges: formattedEdges,
          globalPrompt: flowData.globalPrompt
        }),
        {
          loading: 'Saving Dialog Flow...',
          success: 'Dialog Flow saved successfully',
          error: 'Failed to save Dialog Flow'
        }
      );

      await fetchdialogFlow();
    } catch (err) {
      console.error('Error saving Dialog Flow:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <AppLoadingState blocking label="Loading dialog flow…" />;
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm w-full">
          <div className="text-red-600 text-lg mb-2">⚠️</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/app/dialog-flows')}
            className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg hover:bg-gray-50"
          >
            Back to Dialog Flows
          </button>
        </div>
      </div>
    );
  }

  if (!dialogFlow) return null;

  return (
    <div className="fixed inset-0 flex flex-col bg-white">
      {/* Flow Editor */}
      <div className="flex-1 relative">
        <FlowEditor
          initialNodes={dialogFlow.nodes}
          initialEdges={dialogFlow.edges}
          initialGlobalPrompt={dialogFlow.globalPrompt}
          onSave={handleSave}
          dialogFlowName={dialogFlow.name}
          dialogFlowId={dialogFlow.id}
          onBack={() => navigate('/app/dialog-flows')}
          isSaving={isSaving}
        />
      </div>
    </div>
  );
};

export default DialogFlowDetails; 